import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CalendarDays, MapPin, Clock } from "lucide-react";
import { useFetch } from "../lib/useData";
import { PageHero, Section } from "../components/public/PublicLayout";

const HERO = "https://images.unsplash.com/photo-1699637568981-4a79d177e025?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export function Events() {
  const { data: events } = useFetch("/public/events");
  return (
    <div>
      <PageHero title="Events & Festivals" subtitle="Celebrations and special observances at the temple" image={HERO} />
      <Section>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
          {(events || []).map((ev) => (
            <Link to={`/happenings/events/${ev.slug}`} key={ev.id} className="group bg-white rounded-2xl overflow-hidden border border-amber-200/70 hover:shadow-lg transition-all" data-testid={`event-${ev.slug}`}>
              <div className="h-48 overflow-hidden relative">
                <img src={ev.cover_image} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-3 left-3 text-xs bg-orange-600 text-white rounded-full px-3 py-1 font-semibold">{ev.category}</span>
                {ev.featured && <span className="absolute top-3 right-3 text-xs bg-amber-400 text-amber-950 rounded-full px-2 py-1 font-bold">Featured</span>}
              </div>
              <div className="p-5">
                <h3 className="font-display text-xl font-bold text-stone-900">{ev.title}</h3>
                <p className="flex items-center gap-1.5 text-sm text-stone-500 mt-2"><CalendarDays className="w-4 h-4" /> {ev.start_date}{ev.end_date !== ev.start_date ? ` – ${ev.end_date}` : ""}</p>
                <p className="text-sm text-stone-600 mt-2 line-clamp-2">{ev.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}

export function EventDetail() {
  const { slug } = useParams();
  const { data: ev, loading } = useFetch(`/public/events/${slug}`, [slug]);
  if (loading) return <Section>Loading…</Section>;
  if (!ev) return <Section>Event not found.</Section>;
  return (
    <div>
      <PageHero title={ev.title} subtitle={`${ev.category} · ${ev.start_date}`} image={ev.cover_image} />
      <Section className="max-w-4xl">
        <Link to="/happenings/events" className="inline-flex items-center gap-1 text-orange-700 font-semibold mb-6" data-testid="event-back"><ArrowLeft className="w-4 h-4" /> All Events</Link>
        <div className="flex flex-wrap gap-6 text-stone-600 mb-6">
          <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-orange-600" /> {ev.start_date} – {ev.end_date}</span>
          <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-orange-600" /> {ev.start_time} – {ev.end_time}</span>
          <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-orange-600" /> {ev.location}</span>
        </div>
        <p className="text-lg text-stone-600 leading-relaxed whitespace-pre-line">{ev.description}</p>
        {ev.is_utsav && ev.utsav_days?.length > 0 && (
          <div className="mt-10">
            <h2 className="font-display text-2xl font-bold text-stone-900 mb-5">13-Day Programme</h2>
            <div className="space-y-3" data-testid="utsav-days">
              {ev.utsav_days.map((d) => (
                <div key={d.day} className="flex gap-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="w-14 h-14 rounded-xl bg-orange-600 text-white grid place-items-center font-display font-extrabold shrink-0">{d.day}</div>
                  <div>
                    <div className="font-bold text-stone-900">{d.programme} <span className="text-sm font-normal text-stone-500">· {d.date} · {d.time}</span></div>
                    <p className="text-sm text-stone-600 mt-1">{d.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

export function Utsav() {
  const { data: events } = useFetch("/public/events");
  const utsav = (events || []).find((e) => e.is_utsav);
  return (
    <div>
      <PageHero title="Annual Utsav" subtitle="Our grand 13-day celebration" image="https://images.unsplash.com/photo-1635919254233-38ea27301900?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />
      <Section>
        {utsav ? (
          <div>
            <p className="text-lg text-stone-600 max-w-3xl">{utsav.description}</p>
            <div className="space-y-3 mt-8" data-testid="utsav-timeline">
              {(utsav.utsav_days || []).map((d) => (
                <div key={d.day} className="flex gap-4 bg-white border border-amber-200 rounded-xl p-5 hover:shadow-md transition-all">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-600 to-amber-500 text-white grid place-items-center shrink-0">
                    <div className="text-center leading-none"><div className="text-[10px]">DAY</div><div className="font-display text-2xl font-extrabold">{d.day}</div></div>
                  </div>
                  <div>
                    <div className="font-bold text-lg text-stone-900">{d.programme}</div>
                    <p className="text-sm text-stone-500">{d.date} · {d.time}</p>
                    <p className="text-sm text-stone-600 mt-1">{d.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : <p className="text-stone-500">Utsav programme will be published by the temple soon.</p>}
      </Section>
    </div>
  );
}
